import React, { useState } from "react";
import {
  Card,
  IndexTable,
  Button,
  Text,
  BlockStack,
  Loading,
  Box,
  InlineStack,
} from "@medentic/polaris-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useTranslation } from "react-i18next";
import { TableSkeleton } from "@shared/ui";
import { useGetMyCompanies } from "@shared/hooks/common/useGetMyCompanies";
import { HealthcareCompanyType } from "@shared/types/commonTypes";
import { RenderDate } from "@shared/ui";
import { XIcon } from "@medentic/polaris-icons";

const AccessCard: React.FC = () => {
  const { t } = useTranslation();
  const { DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const [leavingCompanyId, setLeavingCompanyId] = useState<string | null>(null);

  const { companies = [], isLoading, isFetching } = useGetMyCompanies({
    type: HealthcareCompanyType.DENTAL_CLINIC,
    enabled: true,
  });

  const leaveMutation = useMutation({
    mutationFn: (hccId: string) => {
      setLeavingCompanyId(hccId);
      return DELETE<{ message: string }>("/api/user/me/teams/leave", false, {
        hccId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["healthcare-companies", HealthcareCompanyType.DENTAL_CLINIC] });
      setLeavingCompanyId(null);
    },
    onError: () => {
      setLeavingCompanyId(null);
    },
  });

  return (
    <Card padding={"0"}>
      {isFetching && <Loading />}
      <BlockStack gap="400">
        <Box padding={"400"} paddingBlockEnd={"0"}>
          <BlockStack gap="200">
            <Text as="h1" variant="headingMd">
              {t("teamsAccess.access.title")}
            </Text>
            <Text variant="bodyMd" as="p">
              {t("teamsAccess.access.description")}
            </Text>
          </BlockStack>
        </Box>
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <IndexTable
            resourceName={{
              singular: t("common.company"),
              plural: t("common.companies"),
            }}
            itemCount={companies.length}
            headings={[
              { title: t("common.company") },
              { title: t("common.role") },
              { title: t("common.joined") },
              { title: "" },
            ]}
            selectable={false}
          >
            {companies.map((company, index) => (
              <IndexTable.Row id={company.id} key={company.id} position={index}>
                <IndexTable.Cell>{company.name}</IndexTable.Cell>
                <IndexTable.Cell>
                  {company.userPermissions.role}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {company.userPermissions.createdAt && ( 
                    <RenderDate
                      date={company.userPermissions.createdAt}
                        isAgoFormat={true}
                      />
                  )}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <InlineStack align="end">
                    {company.userPermissions.roleId !== 10 && (
                      <Button
                        variant="tertiary"
                        onClick={() => leaveMutation.mutate(company.id)}
                        loading={leavingCompanyId === company.id}
                        size="slim"
                        tone="critical"
                        icon={XIcon}
                      >
                        {t("common.leave")}
                      </Button>
                    )}
                  </InlineStack>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        )}
      </BlockStack>
    </Card>
  );
};

export default AccessCard;
